/**
 * One contact in the address book (#332), as a card or as a compact row.
 *
 * Call and WhatsApp are links built from the stored number, so they render
 * only for a contact that has one. Message, Edit and Delete hand the
 * contact to the page's callbacks.
 */

import {
	Mail,
	MapPin,
	MessageCircle,
	MessageSquareText,
	Pencil,
	Phone,
	Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	type ContactSummary,
	type ContactType,
	telUrl,
	whatsappUrl,
} from "@/features/realtor/services/realtor-crm-api.service";
import { Pill, type PillTone } from "@/features/studio/theme";

export type ContactView = "cards" | "list";

interface ContactEntryProps {
	summary: ContactSummary;
	view: ContactView;
	onMessage: (contactId: string) => void;
	onEdit: (summary: ContactSummary) => void;
	onDelete: (summary: ContactSummary) => void;
}

const TYPE_TONE: Record<ContactType, PillTone> = {
	buyer: "info",
	seller: "active",
	owner: "awaiting",
	builder: "done",
	contractor: "neutral",
};

function formatPhone(countryCode: string, phone: string | null): string | null {
	return phone ? `+${countryCode} ${phone}` : null;
}

export function ContactEntry({
	summary,
	view,
	onMessage,
	onEdit,
	onDelete,
}: ContactEntryProps) {
	const { t } = useTranslation();
	const { contact, leadCount } = summary;
	const phone = formatPhone(contact.phoneCountryCode, contact.phone);
	const tel = telUrl(contact.phoneCountryCode, contact.phone);
	const whatsapp = whatsappUrl(contact.phoneCountryCode, contact.phone);
	const isCard = view === "cards";

	// Reach-the-person actions, labelled on a card. Edit and Delete manage the
	// record rather than reaching anyone, so a card keeps them apart in its
	// top-right corner, which also stops the row wrapping at card width.
	const contactActions = (
		<>
			<ActionButton
				label={t("realtor.contacts.actions.message")}
				ariaLabel={t("realtor.contacts.actions.messageAria", {
					name: contact.name,
				})}
				onClick={() => onMessage(contact.id)}
				icon={<MessageSquareText className="h-3.5 w-3.5" />}
				compact={!isCard}
			/>
			{tel && (
				<a
					href={tel}
					aria-label={t("realtor.contacts.actions.callAria", {
						name: contact.name,
					})}
					className={ACTION_CLASS}
				>
					<Phone className="h-3.5 w-3.5" />
					{isCard && t("realtor.contacts.actions.call")}
				</a>
			)}
			{whatsapp && (
				<a
					href={whatsapp}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={t("realtor.contacts.actions.whatsappAria", {
						name: contact.name,
					})}
					className={ACTION_CLASS}
				>
					<MessageCircle className="h-3.5 w-3.5" />
					{isCard && t("realtor.contacts.actions.whatsapp")}
				</a>
			)}
		</>
	);

	const manageActions = (
		<>
			<ActionButton
				label={t("realtor.contacts.actions.edit")}
				ariaLabel={t("realtor.contacts.actions.editAria", {
					name: contact.name,
				})}
				onClick={() => onEdit(summary)}
				icon={<Pencil className="h-3.5 w-3.5" />}
				compact
			/>
			<ActionButton
				label={t("realtor.contacts.actions.delete")}
				ariaLabel={t("realtor.contacts.actions.deleteAria", {
					name: contact.name,
				})}
				onClick={() => onDelete(summary)}
				icon={<Trash2 className="h-3.5 w-3.5" />}
				compact
			/>
		</>
	);

	const heading = (
		<div className="flex min-w-0 flex-wrap items-center gap-2">
			<p className="min-w-0 truncate text-sm font-medium text-dashboard-text">
				{contact.name}
			</p>
			<Pill tone={TYPE_TONE[contact.type]} style={{ height: 22, fontSize: 11 }}>
				{t(`realtor.contacts.types.${contact.type}`)}
			</Pill>
			{leadCount > 0 && (
				<span className="text-[11px] font-medium text-dashboard-accent-dark">
					{t("realtor.contacts.leadCount", { count: leadCount })}
				</span>
			)}
		</div>
	);

	if (!isCard) {
		return (
			<li
				data-testid={`contact-${contact.id}`}
				className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
			>
				<div className="min-w-0 space-y-0.5">
					{heading}
					<p className="truncate text-xs text-dashboard-muted">
						{[phone, contact.email, contact.city].filter(Boolean).join(" · ")}
					</p>
				</div>
				<div className="flex shrink-0 flex-wrap items-center gap-1.5">
					{contactActions}
					{manageActions}
				</div>
			</li>
		);
	}

	return (
		<li
			data-testid={`contact-${contact.id}`}
			className="flex min-w-0 flex-col gap-3 rounded-xl border border-dashboard-border bg-dashboard-bg p-4 shadow-[var(--dashboard-shadow-2)]"
		>
			<div className="flex items-start justify-between gap-2">
				{heading}
				<div className="flex shrink-0 items-center gap-1.5">
					{manageActions}
				</div>
			</div>
			<div className="min-w-0 space-y-1 text-xs text-dashboard-muted">
				{phone && <Detail icon={<Phone className="h-3 w-3" />} value={phone} />}
				{contact.email && (
					<Detail icon={<Mail className="h-3 w-3" />} value={contact.email} />
				)}
				{contact.city && (
					<Detail icon={<MapPin className="h-3 w-3" />} value={contact.city} />
				)}
			</div>
			{contact.notes && (
				<p className="line-clamp-2 break-words text-xs text-dashboard-text/80">
					{contact.notes}
				</p>
			)}
			<div className="mt-auto flex flex-wrap items-center gap-1.5">
				{contactActions}
			</div>
		</li>
	);
}

const ACTION_CLASS =
	"inline-flex h-8 items-center gap-1.5 rounded-lg border border-dashboard-border px-2.5 text-xs font-medium text-dashboard-text transition-colors hover:bg-dashboard-surface";

function ActionButton({
	label,
	ariaLabel,
	onClick,
	icon,
	compact,
}: {
	label: string;
	ariaLabel: string;
	onClick: () => void;
	icon: React.ReactNode;
	compact?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={ariaLabel}
			title={label}
			className={ACTION_CLASS}
		>
			{icon}
			{!compact && label}
		</button>
	);
}

function Detail({ icon, value }: { icon: React.ReactNode; value: string }) {
	return (
		<div className="flex min-w-0 items-center gap-1.5">
			<span className="shrink-0" aria-hidden="true">
				{icon}
			</span>
			<span className="min-w-0 truncate">{value}</span>
		</div>
	);
}
